import {_} from "lodash";
import {IAuthenticationType, ICompany} from "../../../index";
import {AuthenticationTypeFlag} from "../../../constants/AuthenticationTypeFlag";
import {IdentityProviderProtocol} from "../../../constants/IdentityProviderProtocol";
import {isBit, validateObjectGuid} from "../../../util/misc";

export type BuiltinRole = "primary" | "secondary";

export interface BuiltinDraft {
    flag: number
    role: BuiltinRole
    tenantId: string
    googleId: string
    apiKey: string
    serviceKey: string
}

export type BuiltinChange = { company: ICompany } | { error: string };

export function protocolKey(flag: number): string {
    switch (flag) {
        case AuthenticationTypeFlag.AAD_FEDERATION:
        case AuthenticationTypeFlag.ID_PORTEN:
            return "sso.idp.protocol.oidc";
        case AuthenticationTypeFlag.GOOGLE:
        case AuthenticationTypeFlag.TELIA_FINLAND:
        case AuthenticationTypeFlag.CUSTOM_SAML:
            return "sso.idp.protocol.saml";
        case AuthenticationTypeFlag.GRAND_ID_BANKID_SE:
            return "sso.idp.protocol.grandid";
        default:
            return "sso.idp.protocol.eadm";
    }
}

export function authProtocolKey(protocol: IdentityProviderProtocol): string {
    switch (protocol) {
        case IdentityProviderProtocol.OIDC:
            return "sso.idp.protocol.oidc";
        case IdentityProviderProtocol.SAML:
            return "sso.idp.protocol.saml";
        default:
            return "";
    }
}

export function applyBuiltinSelection(company: ICompany, catalog: IAuthenticationType[], draft: BuiltinDraft): BuiltinChange {
    const type = catalog.find(item => item.flag === draft.flag);
    if (!type || draft.flag === AuthenticationTypeFlag.CUSTOM_SAML) {
        return {error: "sso.idp.builtin.unknown"};
    }
    if (draft.role === "secondary" && company.authenticationtype?.flag === draft.flag) {
        return {error: "sso.idp.builtin.keepprimary"};
    }

    const next = _.cloneDeep(company);
    next.authenticationtypes = company.authenticationtypes | draft.flag;
    if (draft.role === "primary") {
        next.authenticationtype = type;
    }
    if (draft.flag === AuthenticationTypeFlag.AAD_FEDERATION) {
        const tenantId = draft.tenantId.trim();
        if (!validateObjectGuid(tenantId)) {
            return {error: "Invalid s|s:((GUID))"};
        }
        next.ssotenantid = tenantId;
    }
    if (draft.flag === AuthenticationTypeFlag.GOOGLE) {
        const googleId = draft.googleId.trim();
        if (!googleId) {
            return {error: "Invalid value"};
        }
        next.ssogoogleidpid = googleId;
    }
    if (draft.flag === AuthenticationTypeFlag.GRAND_ID_BANKID_SE) {
        const apiKey = draft.apiKey.trim();
        const serviceKey = draft.serviceKey.trim();
        if (!apiKey || !serviceKey) {
            return {error: "Invalid value"};
        }
        next.grandidapikey = apiKey;
        next.grandidservicekey = serviceKey;
    }
    return {company: next};
}

export function removeBuiltin(company: ICompany, flag: number): BuiltinChange {
    if (company.authenticationtype?.flag === flag) {
        return {error: "sso.idp.builtin.keepprimary"};
    }
    if (!isBit(flag, company.authenticationtypes)) {
        return {company};
    }
    const next = _.cloneDeep(company);
    next.authenticationtypes = company.authenticationtypes & ~flag;
    if (flag === AuthenticationTypeFlag.AAD_FEDERATION) {
        next.ssotenantid = "";
    }
    if (flag === AuthenticationTypeFlag.GOOGLE) {
        next.ssogoogleidpid = "";
    }
    if (flag === AuthenticationTypeFlag.GRAND_ID_BANKID_SE) {
        next.grandidapikey = "";
        next.grandidservicekey = "";
    }
    return {company: next};
}
